import { useState, useEffect, useRef } from 'react';

const split = (distance) => ({
  days: Math.floor(distance / 86400000),
  hours: Math.floor((distance % 86400000) / 3600000),
  minutes: Math.floor((distance % 3600000) / 60000),
  seconds: Math.floor((distance % 60000) / 1000),
  remainingMs: distance,
  expired: distance <= 0,
});

/**
 * Countdown to `targetTimestamp` (ms) on the chain's clock: `offsetMs` is chain time minus local time, so the
 * countdown matches the contract even when the user's clock (or a local test chain) is off.
 * @param {number|null} targetTimestamp - target in ms
 * @param {Function} [onComplete] - called once when the countdown reaches zero
 * @param {number} [offsetMs]
 */
const useCountdown = (targetTimestamp, onComplete, offsetMs = 0) => {
  const [countdown, setCountdown] = useState(split(0));
  const completed = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    completed.current = false;
    if (!targetTimestamp) {
      setCountdown({ ...split(0), expired: false });
      return undefined;
    }
    const update = () => {
      const distance = Math.max(0, targetTimestamp - (Date.now() + offsetMs));
      setCountdown(split(distance));
      if (distance <= 0 && !completed.current) {
        completed.current = true;
        if (onCompleteRef.current) onCompleteRef.current();
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetTimestamp, offsetMs]);

  return countdown;
};

export default useCountdown;
